interface ChecklistItem {
  id: string;
  label: string;
  description: string;
  done: boolean;
  icon: string;
}

interface Props {
  isAuthorized: boolean;
  engagementSigned: boolean;
  form5291Submitted: boolean;
  form5297Submitted: boolean;
  cbpDocsSubmitted: boolean;
  retainerFunded: boolean;
  onGoToForm5291?: () => void;
  onGoToForm5297?: () => void;
  onGoToCBPDocs?: () => void;
  onGoToFundRetainer?: () => void;
}

export default function PreFilingChecklist({
  isAuthorized,
  engagementSigned,
  form5291Submitted,
  form5297Submitted,
  cbpDocsSubmitted,
  retainerFunded,
  onGoToForm5291,
  onGoToForm5297,
  onGoToCBPDocs,
  onGoToFundRetainer,
}: Props) {
  const items: ChecklistItem[] = [
    {
      id: 'authorization',
      label: 'Account Authorization',
      description: 'Admin has reviewed and authorized your account',
      done: isAuthorized,
      icon: 'ri-shield-user-line',
    },
    {
      id: 'engagement',
      label: 'Engagement Letter Signed',
      description: 'ESIGN/UETA compliant electronic signature completed',
      done: engagementSigned,
      icon: 'ri-quill-pen-line',
    },
    {
      id: 'retainer',
      label: 'Retainer Funded',
      description: 'Fund your retainer account to secure legal representation',
      done: retainerFunded,
      icon: 'ri-money-dollar-circle-line',
    },
    {
      id: 'form5291',
      label: 'CBP Form 5291 Submitted',
      description: 'Power of Attorney — Designate Attorney Thabit as your CBP Customs Agent',
      done: form5291Submitted,
      icon: 'ri-user-star-line',
    },
    {
      id: 'form5297',
      label: 'CBP Form 4811 Submitted',
      description: 'Importer ID Input Record — DHS/CBP OMB No. 1651-0015',
      done: form5297Submitted,
      icon: 'ri-government-line',
    },
    {
      id: 'cbp_docs',
      label: 'Required CBP Documents Uploaded',
      description: 'Form 7501, proof of duty payment, commercial invoice & Form 4811',
      done: cbpDocsSubmitted,
      icon: 'ri-folder-open-line',
    },
  ];

  const completedCount = items.filter((i) => i.done).length;
  const totalCount = items.length;
  const progressPct = Math.round((completedCount / totalCount) * 100);
  const allDone = completedCount === totalCount;

  return (
    <div className={`backdrop-blur-md rounded-2xl overflow-hidden border-2 ${allDone ? 'bg-green-500/10 border-green-500/40' : 'bg-white/5 border-white/10'}`}>
      {/* Header */}
      <div className={`px-6 py-5 border-b ${allDone ? 'border-green-500/20' : 'border-white/10'}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl border ${allDone ? 'bg-green-500/20 border-green-500/40' : 'bg-[#FDB913]/20 border-[#FDB913]/40'}`}>
              <i className={`${allDone ? 'ri-checkbox-circle-fill text-green-400' : 'ri-file-list-3-line text-[#FDB913]'} text-xl`}></i>
            </div>
            <div>
              <h3 className="text-lg font-black text-white">Pre-Filing Readiness</h3>
              <p className="text-white/50 text-xs">Required before your case can be filed with CBP</p>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full border text-xs font-black ${
            allDone
              ? 'bg-green-500/20 border-green-500/40 text-green-400'
              : completedCount >= 2
              ? 'bg-[#FDB913]/20 border-[#FDB913]/40 text-[#FDB913]'
              : 'bg-white/10 border-white/20 text-white/60'
          }`}>
            {completedCount}/{totalCount} Complete
          </div>
        </div>
        <div className="w-full bg-white/10 rounded-full h-1.5">
          <div
            className={`h-1.5 rounded-full transition-all duration-700 ${allDone ? 'bg-green-400' : 'bg-gradient-to-r from-[#FDB913] to-[#e6a800]'}`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
        {allDone && (
          <p className="text-green-400 text-xs font-bold mt-2 flex items-center gap-1">
            <i className="ri-checkbox-circle-fill"></i>
            All requirements met — your case is ready to be filed!
          </p>
        )}
      </div>

      {/* Items */}
      <div className="divide-y divide-white/5">
        {items.map((item) => (
          <div
            key={item.id}
            className={`flex items-start gap-4 px-6 py-4 transition-all ${item.done ? '' : 'opacity-80'}`}
          >
            <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 mt-0.5 border ${
              item.done ? 'bg-green-500/20 border-green-500/40' : 'bg-white/5 border-white/10'
            }`}>
              {item.done ? (
                <i className="ri-checkbox-circle-fill text-green-400 text-lg"></i>
              ) : (
                <i className={`${item.icon} text-white/30 text-base`}></i>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className={`text-sm font-black ${item.done ? 'text-white' : 'text-white/50'}`}>
                  {item.label}
                </p>
                {item.done ? (
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-green-400 bg-green-500/10 border border-green-500/20 px-2 py-0.5 rounded-full whitespace-nowrap">
                    <i className="ri-check-line text-xs"></i>Complete
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-white/30 bg-white/5 border border-white/10 px-2 py-0.5 rounded-full whitespace-nowrap">
                    <i className="ri-time-line text-xs"></i>Pending
                  </span>
                )}
              </div>
              <p className={`text-xs mt-0.5 leading-relaxed ${item.done ? 'text-white/40' : 'text-white/30'}`}>
                {item.description}
              </p>

              {/* Action buttons */}
              {item.id === 'form5291' && !item.done && onGoToForm5291 && (
                <button
                  onClick={onGoToForm5291}
                  className="mt-2 inline-flex items-center gap-1.5 bg-[#FDB913]/15 hover:bg-[#FDB913]/25 border border-[#FDB913]/40 text-[#FDB913] font-black text-xs px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-user-star-line text-xs"></i>
                  Complete CBP Form 5291
                  <i className="ri-arrow-right-line text-xs"></i>
                </button>
              )}
              {item.id === 'retainer' && !item.done && onGoToFundRetainer && (
                <button
                  onClick={onGoToFundRetainer}
                  className="mt-2 inline-flex items-center gap-1.5 bg-[#FDB913]/15 hover:bg-[#FDB913]/25 border border-[#FDB913]/40 text-[#FDB913] font-black text-xs px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-money-dollar-circle-line text-xs"></i>
                  Fund Retainer Now
                  <i className="ri-arrow-right-line text-xs"></i>
                </button>
              )}
              {item.id === 'form5297' && !item.done && onGoToForm5297 && (
                <button
                  onClick={onGoToForm5297}
                  className="mt-2 inline-flex items-center gap-1.5 bg-[#FDB913]/15 hover:bg-[#FDB913]/25 border border-[#FDB913]/40 text-[#FDB913] font-black text-xs px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-government-line text-xs"></i>
                  Complete CBP Form 4811
                  <i className="ri-arrow-right-line text-xs"></i>
                </button>
              )}
              {item.id === 'cbp_docs' && !item.done && onGoToCBPDocs && (
                <button
                  onClick={onGoToCBPDocs}
                  className="mt-2 inline-flex items-center gap-1.5 bg-[#FDB913]/15 hover:bg-[#FDB913]/25 border border-[#FDB913]/40 text-[#FDB913] font-black text-xs px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-folder-open-line text-xs"></i>
                  Upload CBP Documents
                  <i className="ri-arrow-right-line text-xs"></i>
                </button>
              )}
            </div>

            <div className="flex-shrink-0 mt-1">
              {item.done ? (
                <i className="ri-check-double-line text-green-400 text-base"></i>
              ) : (
                <div className="w-3 h-3 rounded-full border-2 border-white/20 mt-1"></div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
